/**
 * Kostenformel und Standort-Auflösung der Ferienbetreuung (#1063).
 *
 * Doppelt gehalten: diese Datei und `functions/src/_shared/holiday-care-cost.utils.ts` sind bis auf den Import-Pfad identisch,
 * `apps/parity-tests/holiday-care-cost.parity.spec.ts` sichert die Gleichheit.
 * Beide Kopien immer gemeinsam ändern.
 */
import type { HolidayCareLocation } from '../models/holiday-care-registration.model';

/** Eine Woche, wie sie in die Kostenrechnung eingeht (Buchung und Angebot). */
export interface CostWeekInput {
  /** Mittagessen für diese Woche gewünscht. */
  lunch?: boolean;
  /** Mittagessen in dieser Woche buchbar; fehlend gilt als buchbar. */
  lunchAvailable?: boolean;
}

export interface HolidayCareCostResult {
  care: number;
  lunch: number;
  total: number;
}

function toCents(value: number): number {
  return Math.round(value * 100) / 100;
}

function finiteOrZero(value: number | undefined | null): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Kosten einer Ferienbuchung: Gebühr je Woche plus Mittagessen je Woche mit
 * Essen (#1063). Essen zählt nur, wenn die Woche es anbietet
 * (`lunchAvailable !== false`). Nicht-endliche Preise zählen als 0, alle
 * Beträge sind auf Cent gerundet.
 */
export function calculateHolidayCareCosts(
  weeks: CostWeekInput[],
  costPerWeek: number | undefined | null,
  lunchCostPerWeek: number | undefined | null
): HolidayCareCostResult {
  const care = toCents(weeks.length * finiteOrZero(costPerWeek));
  const lunchWeeks = weeks.filter(
    (week) => week.lunch === true && week.lunchAvailable !== false
  ).length;
  const lunch = toCents(lunchWeeks * finiteOrZero(lunchCostPerWeek));
  return { care, lunch, total: toCents(care + lunch) };
}

/**
 * Standort einer Ferienwoche auflösen: `undefined` = eigene Schule,
 * `null` = noch nicht festgelegt, sonst der hinterlegte Standort.
 */
export function resolveWeekLocation(
  week: { location?: HolidayCareLocation | null },
  ownSchool: { id: string; schoolName: string }
): HolidayCareLocation | null {
  if (week.location === undefined) {
    return { schoolId: ownSchool.id, schoolName: ownSchool.schoolName };
  }
  return week.location;
}
